"use client";
import React from 'react';
import { 
  FaCogs, 
  FaShieldAlt, 
  FaDraftingCompass,
  FaArrowRight 
} from 'react-icons/fa';

interface SystemFeature {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  link?: string;
}

const systemsData: SystemFeature[] = [
  {
    id: 1,
    icon: <FaCogs className="w-8 h-8 text-[#ffa20d]" />,
    title: 'Ash Handling Systems',
    description: 'Our world-class infrastructure, state-of-the-art machinery and sound systems and processes enable us to produce batch after batch of high-quality products and maintain consistent standards of service that our customers rely upon.',
    link: '#',
  },
  {
    id: 2,
    icon: <FaShieldAlt className="w-8 h-8 text-[#ffa20d]" />,
    title: 'Coal Mill Reject Handling Systems',
    description: 'At Melco India, we have expertise in efficient handling of mill rejects generated by coal mills in an eco-friendly manner. We are the pioneers in deploying Dense Phase Pneumatic Conveying...',
    link: '#',
  },
  {
    id: 3,
    icon: <FaDraftingCompass className="w-8 h-8 text-[#ffa20d]" />,
    title: 'Ash Water Recirculation Systems',
    description: 'As an industry veteran with sophisticated systems and equipment needed to cater to a wide range of services in ash handling, Melco India also has proven expertise and success in Recovery Water systems.',
    link: '#',
  },
];

export default function SystemsSection() {
  return (
    <section className="relative w-full py-28 px-4 md:px-8 bg-cover bg-center bg-no-repeat overflow-hidden border-none outline-none" 
             style={{ backgroundImage: `linear-gradient(rgba(14, 20, 22, 0.77), rgba(14, 20, 22, 0.77)), url('/assets/background/banner-slider.webp')` }}>
      
      {/* Top Notch / Cut Shape */}
      <div 
        className="absolute -top-px left-1/2 -translate-x-1/2 w-48 sm:w-64 h-8 bg-white pointer-events-none z-30"
        style={{ clipPath: 'polygon(0 0, 100% 0, 80% 100%, 20% 100%)' }}
      />

      <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
        {/* Section Header */}
        <span className="text-[#ffa20d] font-bold text-sm tracking-wider uppercase block mb-1">
          SYSTEMS & FEATURES
        </span>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight whitespace-pre-line">
          Why Choose Our Systems
        </h2>
        <p className="max-w-2xl mx-auto text-white text-xs sm:text-sm leading-relaxed mb-12 mt-3">
          Delivering field-tested, efficient, and heavy-duty industrial systems engineered over three decades of core manufacturing expertise.
        </p>

        {/* Grid Container */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {systemsData.map((item) => (
            <div
              key={item.id}
              className="group relative bg-[#1c2229]/60 p-6 sm:p-8 flex flex-col items-center text-center shadow-xl overflow-hidden transition-all duration-300"
            >
              {/* Base Dashed Frame */}
              <div className="absolute inset-3 border border-dashed border-gray-400/40 pointer-events-none" />

              {/* Initial Top-Left Corner Accent */}
              <div className="absolute top-3 left-3 w-5 h-5 border-t border-l border-[#ffa20d] transition-opacity duration-200 group-hover:opacity-0 pointer-events-none z-10" />

              {/* Initial Bottom-Right Corner Accent */}
              <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-[#ffa20d] transition-opacity duration-200 group-hover:opacity-0 pointer-events-none z-10" />

              {/* --- ULTRA-THIN HOVER ANIMATED BORDER LINES (1px) --- */}
              <span className="absolute top-3 left-3 right-3 h-[1px] bg-[#ffa20d] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-in-out pointer-events-none z-20" />
              <span className="absolute bottom-3 left-3 right-3 h-[1px] bg-[#ffa20d] origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-in-out pointer-events-none z-20" />
              <span className="absolute top-3 bottom-3 left-3 w-[1px] bg-[#ffa20d] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-300 ease-in-out pointer-events-none z-20" />
              <span className="absolute top-3 bottom-3 right-3 w-[1px] bg-[#ffa20d] origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-300 ease-in-out pointer-events-none z-20" />

              {/* Icon */}
              <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300 z-10">
                {item.icon}
              </div>

              {/* Card Title */}
              <h3 className="text-white text-base sm:text-lg font-bold mb-3 tracking-wide z-10">
                {item.title}
              </h3>

              {/* Card Description */}
              <p className="text-gray-300 text-xs leading-relaxed mb-6 flex-grow z-10">
                {item.description}
              </p>

              {/* Read More Button */}
              <a
                href={item.link || "#"}
                className="inline-flex items-center gap-2 text-[#ffa20d] hover:text-white font-semibold text-xs tracking-wider uppercase transition-colors duration-300 mt-auto z-30"
              >
                <span>Read More</span>
                <FaArrowRight className="w-3 h-3 transform group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Notch / Cut Shape */}
      <div 
        className="absolute -bottom-px left-1/2 -translate-x-1/2 w-48 sm:w-64 h-8 bg-white pointer-events-none z-30"
        style={{ clipPath: 'polygon(20% 0, 80% 0, 100% 100%, 0 100%)' }}
      />
    </section>
  );
}